import { useQuery, useMutation, queryCache } from 'react-query'
import { getMetadataFields } from '../../../Recordings/metadataFields.api'
import { Goal, ContactTypeWithClassifiers, contactQueueErrorMessages } from '../constants'
import axios, { AxiosError } from 'axios'
import { useHistory } from 'react-router-dom'

export function useMetadataFieldsQuery() {
  return useQuery({
    queryKey: ['metadataFields'],
    queryFn: async () => {
      const res = await getMetadataFields()
      return Array.isArray(res) ? res : [res]
    },
    config: {
      staleTime: 1000 * 60 * 20,
      cacheTime: 1000 * 60 * 20,
    },
  })
}

export function useCreateGoal() {
  const history = useHistory()
  const createContactGoalEndpoint = '/api/rest/recording/contactGoal'

  return useMutation(
    async (values: Goal) => {
      const res = await axios.post(createContactGoalEndpoint, values)
      if (res.status !== 200) {
        throw new Error('Failed to create goal')
      }
      return res.data
    },
    {
      onSuccess: () => {
        history.push('/admin/contactGoal')
      },
      onError: error => {
        const status = error instanceof AxiosError && error.response?.status ? error.response.status : undefined
        const errorMessage = contactQueueErrorMessages.find(e => e.status === status)?.message
        throw new Error(errorMessage)
      },
    },
  )
}

export function useContactTypeWithClassifiersQuery() {
  const contactTypeEndpoint = '/api/rest/recording/contactGoalContactType?includeClassifiers=true'
  return useQuery<ContactTypeWithClassifiers[] | []>({
    queryKey: ['contactTypeWithClasifiers'],
    queryFn: async () => {
      const res = await axios.get<ContactTypeWithClassifiers[]>(contactTypeEndpoint)
      return Array.isArray(res.data) ? res.data.filter(({ id }) => id !== 4) : []  // Filter out the contact type 'Specific Contact' (id: 4)
    },
  })
}

export function useContactGoalConfigQuery(goalId: string) {
  const contactGoalConfigEndpoint = `/api/rest/recording/contactGoal/${goalId}`
  return useQuery<Goal | null>({
    queryKey: ['ContactGoalConfig', goalId],
    queryFn: async () => {
      if (!goalId) {
        return null
      }
      const res = await axios.get<Goal>(contactGoalConfigEndpoint)
      return Array.isArray(res.data) ? res.data[0] : res.data
    },
  })
}

export function useUpdateGoal() {
  const history = useHistory()
  const updateContactGoalEndpoint = '/api/rest/recording/contactGoal'

  return useMutation(
    async ({ goalId, values }: { goalId: string, values: Goal }) => {
      const res = await axios.put<Omit<Goal, 'people'>>(`${updateContactGoalEndpoint}/${goalId}`, values)
      if (res.status !== 200) {
        throw new Error('Failed to update goal')
      }
      return res.data
    },
    {
      onSuccess: (_, { goalId }) => {
        queryCache.invalidateQueries(['ContactGoalConfigs'])
        queryCache.invalidateQueries(['ContactGoalConfig', goalId])
        history.push('/admin/contactGoal')
      },
      onError: error => {
        const status = error instanceof AxiosError && error.response?.status ? error.response.status : undefined
        const errorMessage = contactQueueErrorMessages.find(e => e.status === status)?.message
        throw new Error(errorMessage)
      },
    },
  )
}
